import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Phone, ShieldCheck, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PhoneOtpVerifier
 * Controlled component that owns its own OTP session state.
 *
 * Props:
 *   phone            (string)     — the mobile number to verify (10-digit or with country code)
 *   onPhoneChange    (fn)         — called when the user edits the phone number
 *   verified         (bool)       — whether phone is already verified in parent state
 *   onVerified       (fn)         — called with (verify_token, normalized_phone) once verified
 *   disabled         (bool)       — external disable
 *   enabled          (bool)       — global OTP feature flag from settings (if false, this shows only the phone input)
 *
 * data-testid coverage: otp-send-btn, otp-code-input, otp-verify-btn, otp-resend-btn, otp-verified-badge
 */
export default function PhoneOtpVerifier({
  phone,
  onPhoneChange,
  verified,
  onVerified,
  disabled = false,
  enabled = true,
}) {
  const [sessionId, setSessionId] = useState(null);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);
  const [devOtp, setDevOtp] = useState(null);
  const [providerMode, setProviderMode] = useState(null);

  useEffect(() => {
    if (cooldown <= 0) {
      clearInterval(cooldownRef.current);
      return;
    }
    cooldownRef.current = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(cooldownRef.current);
  }, [cooldown]);

  // Reset OTP state if the user edits their phone after verifying
  useEffect(() => {
    if (verified) return;
    setSessionId(null);
    setCode("");
  }, [phone]); // eslint-disable-line react-hooks/exhaustive-deps

  const isValidPhone = /^[+\d][\d\s\-]{5,20}$/.test((phone || "").trim());

  const sendOtp = async () => {
    if (!isValidPhone) {
      toast.error("Enter a valid mobile number first.");
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/otp/send`, { phone: phone.trim() });
      setSessionId(r.data.session_id);
      setCooldown(r.data.cooldown_seconds || 30);
      setProviderMode(r.data.provider || "mock");
      if (r.data.dev_otp) {
        setDevOtp(r.data.dev_otp);
        toast.success(`OTP sent (dev mode). Code: ${r.data.dev_otp}`);
      } else {
        setDevOtp(null);
        toast.success(`OTP sent to ${phone.trim()}. Check your SMS.`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not send OTP. Please try again.";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const verifyOtp = async () => {
    if (!sessionId) {
      toast.error("Request an OTP first.");
      return;
    }
    if (!/^\d{4,8}$/.test(code.trim())) {
      toast.error("Enter the code you received.");
      return;
    }
    setVerifying(true);
    try {
      const r = await axios.post(`${API}/otp/verify`, {
        session_id: sessionId,
        phone: phone.trim(),
        code: code.trim(),
      });
      toast.success("Phone verified.");
      onVerified?.(r.data.verify_token, r.data.phone || phone.trim());
    } catch (e) {
      const detail = e?.response?.data?.detail || "Verification failed.";
      toast.error(detail);
    } finally {
      setVerifying(false);
    }
  };

  if (!enabled) {
    // Feature-flag disabled — show phone input only, no OTP UI.
    return (
      <div className="space-y-1.5">
        <label className="text-[11px] tracking-[0.18em] uppercase text-[var(--hk-ink-3)]">Phone</label>
        <input
          value={phone}
          onChange={(e) => onPhoneChange(e.target.value)}
          placeholder="+91 XXXXX XXXXX"
          disabled={disabled}
          data-testid="checkout-field-phone"
          className="h-11 w-full px-3 rounded-md border border-[var(--hk-border)] bg-white/70 focus-visible:ring-[var(--hk-orange)] text-[14px]"
        />
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="phone-otp-block">
      <label className="text-[11px] tracking-[0.18em] uppercase text-[var(--hk-ink-3)]">Phone (OTP verified)</label>
      <div className="flex gap-2 items-stretch">
        <div className="relative flex-1">
          <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--hk-ink-3)]" />
          <input
            value={phone}
            onChange={(e) => onPhoneChange(e.target.value)}
            placeholder="+91 XXXXX XXXXX"
            disabled={disabled || verified}
            data-testid="checkout-field-phone"
            className="h-11 w-full pl-9 pr-3 rounded-md border border-[var(--hk-border)] bg-white/70 focus-visible:ring-[var(--hk-orange)] text-[14px] disabled:opacity-70"
          />
        </div>
        {verified ? (
          <span
            className="inline-flex items-center gap-1.5 px-3 h-11 rounded-md bg-emerald-50 border border-emerald-300 text-emerald-700 text-[12px] font-medium"
            data-testid="otp-verified-badge"
          >
            <CheckCircle2 size={14} /> Verified
          </span>
        ) : (
          <button
            type="button"
            onClick={sendOtp}
            disabled={disabled || sending || cooldown > 0 || !isValidPhone}
            data-testid="otp-send-btn"
            className="hk-btn-ghost h-11 px-3 whitespace-nowrap disabled:opacity-60"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            {sessionId ? (cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP") : (cooldown > 0 ? `Wait ${cooldown}s` : "Send OTP")}
          </button>
        )}
      </div>

      {sessionId && !verified && (
        <div className="mt-2 rounded-md border border-[var(--hk-orange)]/25 bg-[var(--hk-warm-ivory)] p-3 space-y-2" data-testid="otp-code-block">
          <div className="text-[12px] text-[var(--hk-ink-2)]">
            Enter the 6-digit code sent to <span className="font-semibold text-[var(--hk-ink)]">{phone}</span>.
            {devOtp && (
              <span className="ml-1 text-[var(--hk-orange-deep)] font-semibold">Dev code: {devOtp}</span>
            )}
          </div>
          <div className="flex gap-2 items-stretch">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit code"
              inputMode="numeric"
              autoComplete="one-time-code"
              disabled={verifying}
              data-testid="otp-code-input"
              className="h-11 w-40 px-3 rounded-md border border-[var(--hk-border)] bg-white text-[16px] tracking-[0.4em] font-medium focus-visible:ring-[var(--hk-orange)]"
            />
            <button
              type="button"
              onClick={verifyOtp}
              disabled={verifying || code.length < 4}
              data-testid="otp-verify-btn"
              className="hk-btn-primary h-11 px-4 disabled:opacity-60"
            >
              {verifying ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Verify
            </button>
            <button
              type="button"
              onClick={sendOtp}
              disabled={cooldown > 0 || sending}
              data-testid="otp-resend-btn"
              className="hk-btn-ghost h-11 px-3 text-[12px] disabled:opacity-60"
              title={cooldown > 0 ? `Resend in ${cooldown}s` : "Resend"}
            >
              <RefreshCw size={13} /> {cooldown > 0 ? `${cooldown}s` : "Resend"}
            </button>
          </div>
          {providerMode && providerMode.startsWith("mock") && (
            <p className="text-[11px] text-[var(--hk-ink-3)]">Store is in OTP DEV mode — no SMS is actually sent. Add MSG91 credentials in Admin → Integrations to go live.</p>
          )}
        </div>
      )}
    </div>
  );
}
